import React from 'react';
import { useNavigate } from 'react-router-dom';
import type { EscalationNotification, NotificationKind } from '../../utils/useEscalationNotifications';
import { useProxyData } from '../../contexts/ProxyDataContext';
import { useEmailInbox } from '../../contexts/EmailInboxContext';
import { useTeamMessages } from '../../contexts/TeamMessagesContext';
import { buildProxyIntakeTasks, type ProxyTask } from '../../data/proxyWorkflowRegistry';

const NOTIF_ICON: Record<NotificationKind, { bg: string; stroke: string; path: React.ReactNode }> = {
  escalation: {
    bg: '#FEE2E2', stroke: '#DC2626',
    path: <>
      <path d="M4 14V2.5" strokeLinecap="round"/>
      <path d="M4 2.5h6.5l-1.8 2.5 1.8 2.5H4" strokeLinejoin="round"/>
    </>,
  },
  // A message from the other team (Fulfillment ↔ Proxy).
  team: {
    bg: '#EDE9FE', stroke: '#6D28D9',
    path: <>
      <path d="M2.5 3.5h11v7h-6l-3 2.5v-2.5h-2z" strokeLinejoin="round"/>
    </>,
  },
  'new-scan': {
    bg: '#CCFBF1', stroke: '#0D9488',
    path: <>
      <path d="M5.3 5l.9-1.3h3.6L10.7 5" strokeLinejoin="round"/>
      <rect x="2.3" y="5" width="11.4" height="8" rx="1.2"/>
      <circle cx="8" cy="9" r="2.1"/>
    </>,
  },
  exception: {
    bg: '#FEF3C7', stroke: '#D97706',
    path: <>
      <path d="M8 2.5l6 10.5H2L8 2.5z" strokeLinejoin="round"/>
      <path d="M8 6.7v3" strokeLinecap="round"/>
      <circle cx="8" cy="11.6" r="0.7" fill="#D97706" stroke="none"/>
    </>,
  },
  overs: {
    bg: '#EDE9FE', stroke: '#6D28D9',
    path: <>
      <rect x="2.5" y="6.5" width="7" height="7" rx="1"/>
      <path d="M6 6.5V4.5a1 1 0 0 1 1-1h5a1 1 0 0 1 1 1V9a1 1 0 0 1-1 1h-2" strokeLinejoin="round"/>
    </>,
  },
  'followup-due': {
    bg: '#EDE9FE', stroke: '#6D28D9',
    path: <>
      <circle cx="8" cy="8.5" r="5.5"/>
      <path d="M8 5.5v3.2l2 1.3" strokeLinecap="round" strokeLinejoin="round"/>
    </>,
  },
  inbox: {
    bg: '#F3F4F6', stroke: '#063F5A',
    path: <>
      <rect x="2" y="4" width="12" height="8.5" rx="1"/>
      <path d="M2.5 4.8l5.5 4 5.5-4" strokeLinecap="round" strokeLinejoin="round"/>
    </>,
  },
};

function NotifIcon({ kind }: { kind: NotificationKind }) {
  const cfg = NOTIF_ICON[kind] ?? NOTIF_ICON.inbox;
  return (
    <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: cfg.bg }}>
      <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke={cfg.stroke} strokeWidth={1.4}>
        {cfg.path}
      </svg>
    </div>
  );
}

export function NotificationPanel({
  open,
  onClose,
  onMarkAllRead,
  onTeamMessageOpened,
  onItemRead,
  notifications,
  unreadCount,
}: {
  open: boolean;
  onClose: () => void;
  onMarkAllRead: () => void;
  onTeamMessageOpened?: (id: string) => void;
  onItemRead?: (id: string) => void;
  notifications: EscalationNotification[];
  unreadCount: number;
}) {
  const navigate = useNavigate();
  const { jobs } = useProxyData();
  const { proxyEmails } = useEmailInbox();
  const { messages: teamMessages } = useTeamMessages();

  // The open Proxy task a notification is about, worked out at click time.
  function findTask(match: (t: ProxyTask) => boolean): ProxyTask | undefined {
    return buildProxyIntakeTasks({ jobs, emails: proxyEmails, receipts: null, teamMessages }).find(match);
  }

  const titleFor = (n: EscalationNotification) => {
    switch (n.kind) {
      case 'escalation': return `Escalated: Scan #${n.scan}`;
      case 'new-scan':   return `New scan: ${n.scan}`;
      case 'exception':  return `${n.desc}: Scan #${n.scan}`;
      case 'overs':      return `Overs: Scan #${n.scan}`;
      case 'inbox':      return 'New email';
      case 'followup-due': return `Follow-up due: ${n.client} (Job ${n.jobId})`;
      case 'team':       return n.title ?? n.client;
      default:           return n.client;
    }
  };

  // Close the panel, then go to the list the item is on, with its row
  // highlighted. A Proxy task that's already been approved has left the
  // queue, so that goes to the job in Completed instead.
  const handleClick = (n: EscalationNotification) => {
    onClose();
    if (n.kind === 'team') onTeamMessageOpened?.(n.id);
    else onItemRead?.(n.id);
    const href = n.href ?? '/';
    const taskId = new URLSearchParams(href.split('?')[1] ?? '').get('taskId');
    if (taskId && !findTask(t => t.id === taskId)) {
      navigate(`/proxy/completed?q=${encodeURIComponent(n.jobId ?? n.scan ?? '')}`);
      return;
    }
    navigate(href);
  };

  return (
    <>
      {open && (
        <div className="fixed inset-0 z-[90]" onClick={onClose} />
      )}
      <div
        data-tour="notif-panel"
        className="fixed top-0 right-0 h-full z-[100] bg-white border-l border-trim flex flex-col transition-transform duration-300"
        style={{
          width: 360,
          transform: open ? 'translateX(0)' : 'translateX(calc(100% + 2px))',
          boxShadow: open ? '-4px 0 24px rgba(4,50,67,.12)' : 'none',
        }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-trim flex-shrink-0">
          <div className="text-sm font-semibold text-ink">
            Notifications ({unreadCount} unread)
          </div>
          <button
            onClick={onClose}
            className="w-7 h-7 rounded-lg flex items-center justify-center text-copy hover:bg-wash transition-colors"
          >
            <svg width="13" height="13" viewBox="0 0 13 13" fill="none"><path d="M1.5 1.5l10 10M11.5 1.5l-10 10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>
          </button>
        </div>

        {/* List */}
        <div className="flex-1 overflow-y-auto">
          {notifications.length === 0 && (
            <div className="px-5 py-8 text-center text-xs text-muted">No notifications right now.</div>
          )}
          {notifications.map(n => (
            <div
              key={n.id}
              onClick={() => handleClick(n)}
              className="flex gap-3 px-5 py-3.5 border-b border-wash cursor-pointer hover:bg-[#FAFAFA] transition-colors"
              style={{ background: n.unread ? '#F5F3FF' : undefined }}
            >
              <NotifIcon kind={n.kind} />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold text-ink leading-tight truncate">{titleFor(n)}</div>
                <div className="text-xs text-copy mt-0.5 leading-relaxed line-clamp-2">
                  {n.kind === 'inbox' ? `${n.client}: ${n.desc}` : (n.desc || n.client)}
                </div>
                {n.timestamp && <div className="text-xs text-muted mt-1">{n.timestamp}</div>}
              </div>
              {n.unread && (
                <div className="w-1.5 h-1.5 rounded-full bg-teal-500 mt-1 flex-shrink-0" />
              )}
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className="flex gap-2 px-4 py-3 border-t border-trim flex-shrink-0">
          <button
            onClick={onMarkAllRead}
            className="flex-1 py-2 rounded-lg border border-trim text-sm font-medium text-copy hover:bg-wash transition-colors"
          >
            Mark all read
          </button>
        </div>
      </div>
    </>
  );
}
